import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Heart, Lock, Eye, Loader2, ChevronLeft, ChevronRight, X, Search,
  Calendar, Clock, MapPin, Users, Image, MessageSquare, HelpCircle,
  Star, BookOpen, ExternalLink, Upload, ChevronDown, ChevronUp,
  Navigation as NavIcon, Menu, ArrowUp, Camera
} from 'lucide-react';
import { wedding } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bridalconnect.co.za';

// ---------- Helpers ----------

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-ZA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function getImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

function getCountdown(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  const diff = target - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const secs = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, mins, secs };
}

// Template font classes
const TEMPLATE_FONTS = {
  classic: {
    heading: 'font-serif',
    body: 'font-serif',
    hero: 'font-serif italic',
    accent: 'tracking-wide'
  },
  modern: {
    heading: 'font-sans font-bold',
    body: 'font-sans',
    hero: 'font-sans font-black uppercase tracking-widest',
    accent: 'uppercase tracking-widest text-sm'
  },
  rustic: {
    heading: 'font-serif',
    body: 'font-sans',
    hero: 'font-serif',
    accent: 'tracking-wide'
  }
};

function getTemplateFonts(template) {
  return TEMPLATE_FONTS[template] || TEMPLATE_FONTS.classic;
}

// Template-specific container styles
function getTemplateStyles(template) {
  switch (template) {
    case 'modern':
      return { borderRadius: '0px', sectionGap: 'gap-0' };
    case 'rustic':
      return { borderRadius: '16px', sectionGap: 'gap-8' };
    default: // classic
      return { borderRadius: '8px', sectionGap: 'gap-6' };
  }
}

// Section icons lookup
const SECTION_ICONS = {
  hero: Calendar,
  our_story: BookOpen,
  schedule: Clock,
  wedding_party: Users,
  travel: MapPin,
  gallery: Image,
  rsvp: MessageSquare,
  vendors: Star,
  faq: HelpCircle
};

const SECTION_LABELS = {
  hero: 'Home',
  our_story: 'Our Story',
  schedule: 'Schedule',
  wedding_party: 'Wedding Party',
  travel: 'Travel',
  gallery: 'Gallery',
  rsvp: 'RSVP',
  vendors: 'Vendors',
  faq: 'FAQ'
};

// ---------- Password Gate ----------

function PasswordGate({ page, onUnlock, template }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const fonts = getTemplateFonts(template || 'classic');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await wedding.verifyPagePassword(page.slug || page.id, password);
      onUnlock(res.data);
    } catch (err) {
      setError('Incorrect password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Lock className="w-8 h-8 text-rose-500" />
        </div>
        <h1 className={`text-2xl md:text-3xl mb-2 ${fonts.heading}`}>
          {page.partner1_name} & {page.partner2_name}
        </h1>
        <p className="text-gray-500 mb-6">This wedding page is password protected</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-300 focus:border-rose-400 outline-none text-center text-lg"
            autoFocus
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password}
            className="w-full py-3 bg-rose-500 text-white rounded-lg hover:bg-rose-600 disabled:opacity-50 transition-colors font-medium"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'View Wedding Page'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------- Section Nav ----------

function SectionNav({ sections, activeSection, template }) {
  const [open, setOpen] = useState(false);
  const fonts = getTemplateFonts(template);

  const scrollTo = (sectionType) => {
    const el = document.getElementById(`section-${sectionType}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setOpen(false);
    }
  };

  const visibleSections = sections.filter(s => s.is_visible !== false);
  if (visibleSections.length === 0) return null;

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed top-4 right-4 z-50 md:hidden bg-white/90 backdrop-blur-sm shadow-lg rounded-full p-3"
        style={{ color: 'var(--wedding-primary)' }}
      >
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile overlay menu */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden bg-black/50" onClick={() => setOpen(false)}>
          <div
            className="absolute right-0 top-0 h-full w-64 bg-white shadow-xl p-6 pt-16"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="space-y-1">
              {visibleSections.map((s) => {
                const Icon = SECTION_ICONS[s.section_type] || BookOpen;
                return (
                  <button
                    key={s.section_type}
                    onClick={() => scrollTo(s.section_type)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      activeSection === s.section_type
                        ? 'bg-rose-50 text-rose-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    } ${fonts.body}`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {s.title || SECTION_LABELS[s.section_type] || s.section_type}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop nav dots */}
      <nav className="hidden md:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col gap-3">
        {visibleSections.map((s) => (
          <button
            key={s.section_type}
            onClick={() => scrollTo(s.section_type)}
            className="group relative flex items-center justify-end"
            title={s.title || SECTION_LABELS[s.section_type]}
          >
            <span
              className={`absolute right-8 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 text-white ${fonts.body}`}
            >
              {s.title || SECTION_LABELS[s.section_type]}
            </span>
            <span
              className={`w-3 h-3 rounded-full border-2 transition-all ${
                activeSection === s.section_type
                  ? 'scale-125'
                  : 'bg-white border-gray-300 hover:border-gray-500'
              }`}
              style={
                activeSection === s.section_type
                  ? { backgroundColor: 'var(--wedding-primary)', borderColor: 'var(--wedding-primary)' }
                  : {}
              }
            />
          </button>
        ))}
      </nav>
    </>
  );
}

// ---------- Hero Section ----------

function HeroSection({ section, page, template }) {
  const fonts = getTemplateFonts(template);
  const [countdown, setCountdown] = useState(getCountdown(page.wedding_date));

  useEffect(() => {
    if (!page.wedding_date) return;
    const timer = setInterval(() => {
      setCountdown(getCountdown(page.wedding_date));
    }, 1000);
    return () => clearInterval(timer);
  }, [page.wedding_date]);

  const heroImage = section.content?.hero_image_url || page.hero_image_url;
  const bgUrl = getImageUrl(heroImage);

  return (
    <div
      id="section-hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background image */}
      {bgUrl && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgUrl})` }}
        />
      )}
      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: bgUrl
            ? 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.5))'
            : 'var(--wedding-bg, #fdf2f8)'
        }}
      />

      {/* Content */}
      <div className="relative z-10 text-center px-6 py-12 max-w-3xl mx-auto">
        <p
          className={`text-sm md:text-base mb-4 ${fonts.accent} ${bgUrl ? 'text-white/80' : 'text-gray-500'}`}
        >
          {section.content?.subtitle || 'We\'re getting married'}
        </p>

        <h1
          className={`text-4xl md:text-6xl lg:text-7xl mb-4 ${fonts.hero} ${
            bgUrl ? 'text-white' : ''
          }`}
          style={!bgUrl ? { color: 'var(--wedding-primary)' } : {}}
        >
          {page.partner1_name} <span className={`${bgUrl ? 'text-white/70' : 'text-gray-400'} mx-2`}>&</span> {page.partner2_name}
        </h1>

        {page.wedding_date && (
          <p
            className={`text-lg md:text-xl mb-2 ${fonts.body} ${
              bgUrl ? 'text-white/90' : 'text-gray-600'
            }`}
          >
            {formatDate(page.wedding_date)}
          </p>
        )}

        {(page.venue_name || section.content?.venue_name) && (
          <p
            className={`text-base md:text-lg mb-8 flex items-center justify-center gap-2 ${fonts.body} ${
              bgUrl ? 'text-white/80' : 'text-gray-500'
            }`}
          >
            <MapPin className="w-4 h-4" />
            {section.content?.venue_name || page.venue_name}
          </p>
        )}

        {/* Countdown */}
        {countdown && (
          <div className="flex justify-center gap-4 md:gap-6 mt-8">
            {[
              { val: countdown.days, label: 'Days' },
              { val: countdown.hours, label: 'Hours' },
              { val: countdown.mins, label: 'Minutes' },
              { val: countdown.secs, label: 'Seconds' }
            ].map(({ val, label }) => (
              <div
                key={label}
                className={`${
                  bgUrl
                    ? 'bg-white/20 backdrop-blur-sm border border-white/30'
                    : 'bg-white shadow-md border border-gray-100'
                } rounded-xl px-3 py-3 md:px-5 md:py-4 min-w-[60px] md:min-w-[80px]`}
              >
                <div
                  className={`text-2xl md:text-3xl font-bold ${fonts.heading} ${
                    bgUrl ? 'text-white' : ''
                  }`}
                  style={!bgUrl ? { color: 'var(--wedding-primary)' } : {}}
                >
                  {String(val).padStart(2, '0')}
                </div>
                <div
                  className={`text-xs md:text-sm ${
                    bgUrl ? 'text-white/70' : 'text-gray-400'
                  } ${fonts.accent}`}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Scroll indicator */}
        <div className={`mt-12 animate-bounce ${bgUrl ? 'text-white/60' : 'text-gray-300'}`}>
          <ChevronDown className="w-6 h-6 mx-auto" />
        </div>
      </div>
    </div>
  );
}

// ---------- Story Section ----------

function StorySection({ section, template }) {
  const fonts = getTemplateFonts(template);
  const content = section.content || {};
  const photos = content.photos || [];

  return (
    <div id="section-our_story" className="py-16 md:py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <h2
          className={`text-3xl md:text-4xl text-center mb-12 ${fonts.heading}`}
          style={{ color: 'var(--wedding-primary)' }}
        >
          {section.title || 'Our Story'}
        </h2>

        {content.text && (
          <div
            className={`text-gray-600 leading-relaxed text-base md:text-lg whitespace-pre-line text-center max-w-2xl mx-auto mb-10 ${fonts.body}`}
          >
            {content.text}
          </div>
        )}

        {photos.length > 0 && (
          <div
            className={`grid gap-4 mt-8 ${
              photos.length === 1 ? 'grid-cols-1 max-w-lg mx-auto' : 'grid-cols-1 md:grid-cols-2'
            }`}
          >
            {photos.map((photo, i) => (
              <div key={i} className="overflow-hidden rounded-xl shadow-md">
                <img
                  src={getImageUrl(photo.url || photo)}
                  alt={photo.caption || `Our story ${i + 1}`}
                  className="w-full h-64 md:h-80 object-cover"
                  loading="lazy"
                />
                {photo.caption && (
                  <p className={`text-center text-sm text-gray-500 py-2 ${fonts.body}`}>
                    {photo.caption}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Schedule Section ----------

function ScheduleSection({ section, events = [], template }) {
  const fonts = getTemplateFonts(template);

  const sortedEvents = [...events].sort((a, b) => {
    if (a.event_date !== b.event_date) return (a.event_date || '').localeCompare(b.event_date || '');
    return (a.start_time || '').localeCompare(b.start_time || '');
  });

  return (
    <div id="section-schedule" className="py-16 md:py-24 px-6" style={{ backgroundColor: 'var(--wedding-secondary)' }}>
      <div className="max-w-3xl mx-auto">
        <h2
          className={`text-3xl md:text-4xl text-center mb-12 ${fonts.heading}`}
          style={{ color: 'var(--wedding-primary)' }}
        >
          {section.title || 'Schedule'}
        </h2>

        {sortedEvents.length === 0 ? (
          <p className={`text-center text-gray-500 ${fonts.body}`}>Event details coming soon.</p>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div
              className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5"
              style={{ backgroundColor: 'var(--wedding-primary)', opacity: 0.2 }}
            />

            <div className="space-y-8">
              {sortedEvents.map((event, i) => (
                <div key={event.id || i} className="relative flex gap-4 md:gap-6 pl-2">
                  {/* Timeline dot */}
                  <div
                    className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center z-10 shadow-sm"
                    style={{ backgroundColor: 'var(--wedding-primary)' }}
                  >
                    <Clock className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </div>

                  {/* Event card */}
                  <div className="flex-1 bg-white rounded-xl shadow-sm p-5 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-2">
                      <h3 className={`text-lg md:text-xl ${fonts.heading}`} style={{ color: 'var(--wedding-primary)' }}>
                        {event.event_name || event.name}
                      </h3>
                      <div className={`text-sm text-gray-500 ${fonts.accent} flex-shrink-0`}>
                        {event.start_time && formatTime(event.start_time)}
                        {event.end_time && ` - ${formatTime(event.end_time)}`}
                      </div>
                    </div>

                    {event.event_date && event.event_date !== events[0]?.event_date && (
                      <p className={`text-sm text-gray-500 mb-2 ${fonts.body}`}>
                        <Calendar className="w-3.5 h-3.5 inline mr-1" />
                        {formatDate(event.event_date)}
                      </p>
                    )}

                    {(event.venue_name || event.venue) && (
                      <p className={`text-sm text-gray-600 mb-2 flex items-center gap-1 ${fonts.body}`}>
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        {event.venue_name || event.venue}
                      </p>
                    )}

                    {event.dress_code && (
                      <p className={`text-sm text-gray-500 mb-2 ${fonts.body}`}>
                        Dress code: <span className="font-medium text-gray-700">{event.dress_code}</span>
                      </p>
                    )}

                    {event.description && (
                      <p className={`text-sm text-gray-500 mt-2 ${fonts.body}`}>{event.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Party Section ----------

function PartySection({ section, party = [], page, template }) {
  const fonts = getTemplateFonts(template);

  const side1 = party.filter(m => m.side === 'partner1' || m.side === 1);
  const side2 = party.filter(m => m.side === 'partner2' || m.side === 2);
  const noSide = party.filter(m => !m.side || (m.side !== 'partner1' && m.side !== 'partner2' && m.side !== 1 && m.side !== 2));

  const renderMembers = (members) => (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
      {members.map((m, i) => (
        <div key={m.id || i} className="text-center">
          <div className="w-24 h-24 md:w-28 md:h-28 mx-auto mb-3 rounded-full overflow-hidden bg-gray-100 shadow-md">
            {m.photo_url ? (
              <img
                src={getImageUrl(m.photo_url)}
                alt={m.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <Users className="w-8 h-8" />
              </div>
            )}
          </div>
          <h4 className={`font-medium text-gray-800 ${fonts.body}`}>{m.name}</h4>
          <p className={`text-sm text-gray-500 ${fonts.accent}`}>{m.role}</p>
        </div>
      ))}
    </div>
  );

  return (
    <div id="section-wedding_party" className="py-16 md:py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <h2
          className={`text-3xl md:text-4xl text-center mb-12 ${fonts.heading}`}
          style={{ color: 'var(--wedding-primary)' }}
        >
          {section.title || 'Wedding Party'}
        </h2>

        {party.length === 0 ? (
          <p className={`text-center text-gray-500 ${fonts.body}`}>Details coming soon.</p>
        ) : (
          <div className="space-y-12">
            {side1.length > 0 && (
              <div>
                <h3
                  className={`text-xl text-center mb-6 ${fonts.heading}`}
                  style={{ color: 'var(--wedding-primary)', opacity: 0.8 }}
                >
                  {page.partner1_name}'s Side
                </h3>
                {renderMembers(side1)}
              </div>
            )}

            {side2.length > 0 && (
              <div>
                <h3
                  className={`text-xl text-center mb-6 ${fonts.heading}`}
                  style={{ color: 'var(--wedding-primary)', opacity: 0.8 }}
                >
                  {page.partner2_name}'s Side
                </h3>
                {renderMembers(side2)}
              </div>
            )}

            {noSide.length > 0 && renderMembers(noSide)}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Travel Section ----------

function TravelSection({ section, template }) {
  const fonts = getTemplateFonts(template);
  const content = section.content || {};
  const items = content.items || content.accommodations || [];

  return (
    <div id="section-travel" className="py-16 md:py-24 px-6" style={{ backgroundColor: 'var(--wedding-secondary)' }}>
      <div className="max-w-4xl mx-auto">
        <h2
          className={`text-3xl md:text-4xl text-center mb-4 ${fonts.heading}`}
          style={{ color: 'var(--wedding-primary)' }}
        >
          {section.title || 'Travel & Accommodation'}
        </h2>

        {content.description && (
          <p className={`text-center text-gray-600 mb-10 max-w-2xl mx-auto ${fonts.body}`}>
            {content.description}
          </p>
        )}

        {items.length === 0 ? (
          <p className={`text-center text-gray-500 ${fonts.body}`}>Details coming soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {items.map((item, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm p-6">
                <h3 className={`text-lg font-semibold mb-2 ${fonts.heading}`} style={{ color: 'var(--wedding-primary)' }}>
                  {item.name}
                </h3>

                {item.address && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm text-gray-600 hover:text-gray-800 flex items-start gap-1.5 mb-3 ${fonts.body}`}
                  >
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span className="underline decoration-dotted">{item.address}</span>
                  </a>
                )}

                {item.phone && (
                  <p className={`text-sm text-gray-600 mb-2 ${fonts.body}`}>
                    Phone: <a href={`tel:${item.phone}`} className="underline">{item.phone}</a>
                  </p>
                )}

                {item.website && (
                  <a
                    href={item.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm inline-flex items-center gap-1 mb-2 ${fonts.body}`}
                    style={{ color: 'var(--wedding-primary)' }}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Website
                  </a>
                )}

                {item.notes && (
                  <p className={`text-sm text-gray-500 mt-2 ${fonts.body}`}>{item.notes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Gallery Section ----------

function GallerySection({ section, mediaItems = [], slug, template }) {
  const fonts = getTemplateFonts(template);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileRef = useRef(null);

  const approvedMedia = mediaItems.filter(m => m.is_approved !== false);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);

  const handlePrev = () => {
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : approvedMedia.length - 1));
  };
  const handleNext = () => {
    setLightboxIndex((prev) => (prev < approvedMedia.length - 1 ? prev + 1 : 0));
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadMsg('');
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('photo', files[i]);
        await wedding.guestUpload(slug, formData);
      }
      setUploadMsg(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded! The couple will review them.`);
    } catch {
      setUploadMsg('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Masonry-like layout using columns
  const colCount = typeof window !== 'undefined' && window.innerWidth < 768 ? 2 : 3;
  const columns = Array.from({ length: colCount }, () => []);
  approvedMedia.forEach((m, i) => {
    columns[i % colCount].push({ ...m, _index: i });
  });

  return (
    <div id="section-gallery" className="py-16 md:py-24 px-6">
      <div className="max-w-5xl mx-auto">
        <h2
          className={`text-3xl md:text-4xl text-center mb-12 ${fonts.heading}`}
          style={{ color: 'var(--wedding-primary)' }}
        >
          {section.title || 'Gallery'}
        </h2>

        {approvedMedia.length === 0 ? (
          <p className={`text-center text-gray-500 mb-8 ${fonts.body}`}>Photos coming soon.</p>
        ) : (
          <div className="flex gap-3 md:gap-4 mb-10">
            {columns.map((col, ci) => (
              <div key={ci} className="flex-1 flex flex-col gap-3 md:gap-4">
                {col.map((m) => (
                  <button
                    key={m.id || m._index}
                    onClick={() => openLightbox(m._index)}
                    className="overflow-hidden rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer block w-full"
                  >
                    <img
                      src={getImageUrl(m.media_url || m.url)}
                      alt={m.caption || 'Wedding photo'}
                      className="w-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Guest upload */}
        <div className="text-center">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            className="hidden"
            id="guest-upload-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-medium transition-colors"
            style={{ backgroundColor: 'var(--wedding-primary)' }}
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Camera className="w-5 h-5" />
            )}
            {uploading ? 'Uploading...' : 'Share Your Photos'}
          </button>
          {uploadMsg && (
            <p className={`mt-3 text-sm ${uploadMsg.includes('failed') ? 'text-red-500' : 'text-green-600'} ${fonts.body}`}>
              {uploadMsg}
            </p>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && approvedMedia[lightboxIndex] && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); handlePrev(); }}
            className="absolute left-2 md:left-6 text-white/70 hover:text-white p-2"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          <img
            src={getImageUrl(approvedMedia[lightboxIndex].media_url || approvedMedia[lightboxIndex].url)}
            alt={approvedMedia[lightboxIndex].caption || 'Photo'}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
            className="absolute right-2 md:right-6 text-white/70 hover:text-white p-2"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {approvedMedia[lightboxIndex].caption && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm max-w-lg text-center">
              {approvedMedia[lightboxIndex].caption}
            </p>
          )}

          <div className="absolute bottom-4 right-6 text-white/50 text-sm">
            {lightboxIndex + 1} / {approvedMedia.length}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- RSVP Section ----------

function RSVPSection({ section, slug, template }) {
  const fonts = getTemplateFonts(template);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needFullName, setNeedFullName] = useState(false);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError('');
    setLoading(true);
    try {
      const res = await wedding.lookupRsvp(slug, name.trim());
      const data = res.data;
      if (data.multiple) {
        setNeedFullName(true);
        setError('Multiple matches found. Please enter your full name.');
      } else if (data.token) {
        navigate(`/rsvp/${data.token}`);
      } else {
        setError('We couldn\'t find your name on the guest list. Please check the spelling or contact the couple.');
      }
    } catch {
      setError('We couldn\'t find your name on the guest list. Please check the spelling or contact the couple.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="section-rsvp" className="py-16 md:py-24 px-6" style={{ backgroundColor: 'var(--wedding-secondary)' }}>
      <div className="max-w-lg mx-auto text-center">
        <h2
          className={`text-3xl md:text-4xl mb-4 ${fonts.heading}`}
          style={{ color: 'var(--wedding-primary)' }}
        >
          {section.title || 'RSVP'}
        </h2>

        <p className={`text-gray-600 mb-8 ${fonts.body}`}>
          {section.content?.description || 'Find your invitation to let us know if you\'ll be joining us.'}
        </p>

        <form onSubmit={handleLookup} className="space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); setNeedFullName(false); }}
              placeholder={needFullName ? 'Enter your full name' : 'Enter your name'}
              className={`w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none text-lg ${fonts.body}`}
              style={{ '--tw-ring-color': 'var(--wedding-primary)' }}
            />
          </div>

          {error && (
            <p className={`text-sm ${needFullName ? 'text-amber-600' : 'text-red-500'} ${fonts.body}`}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !name.trim()}
            className="w-full py-3 text-white rounded-lg font-medium disabled:opacity-50 transition-colors"
            style={{ backgroundColor: 'var(--wedding-primary)' }}
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              'Find My Invitation'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------- Vendors Section ----------

function VendorsSection({ section, vendors = [], template }) {
  const fonts = getTemplateFonts(template);

  return (
    <div id="section-vendors" className="py-16 md:py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <h2
          className={`text-3xl md:text-4xl text-center mb-12 ${fonts.heading}`}
          style={{ color: 'var(--wedding-primary)' }}
        >
          {section.title || 'Our Vendors'}
        </h2>

        {vendors.length === 0 ? (
          <p className={`text-center text-gray-500 ${fonts.body}`}>Vendor details coming soon.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {vendors.map((v, i) => {
              const isLinked = v.provider_id;
              const Wrapper = isLinked ? Link : v.website ? 'a' : 'div';
              const wrapperProps = isLinked
                ? { to: `/provider/${v.provider_id}` }
                : v.website
                  ? { href: v.website, target: '_blank', rel: 'noopener noreferrer' }
                  : {};

              return (
                <Wrapper
                  key={v.id || i}
                  {...wrapperProps}
                  className="bg-white rounded-xl shadow-sm p-4 md:p-5 text-center hover:shadow-md transition-shadow block"
                >
                  {v.logo_url || v.provider_logo ? (
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden bg-gray-50">
                      <img
                        src={getImageUrl(v.logo_url || v.provider_logo)}
                        alt={v.vendor_name || v.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div
                      className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--wedding-secondary)' }}
                    >
                      <Star className="w-6 h-6" style={{ color: 'var(--wedding-primary)' }} />
                    </div>
                  )}

                  <h3 className={`font-medium text-gray-800 text-sm md:text-base ${fonts.body}`}>
                    {v.vendor_name || v.name || v.provider_name}
                  </h3>

                  {(v.category || v.vendor_category) && (
                    <p className={`text-xs text-gray-500 mt-1 ${fonts.accent}`}>
                      {v.category || v.vendor_category}
                    </p>
                  )}

                  {isLinked && (
                    <span
                      className="inline-flex items-center gap-1 text-xs mt-2"
                      style={{ color: 'var(--wedding-primary)' }}
                    >
                      View Profile <ExternalLink className="w-3 h-3" />
                    </span>
                  )}
                </Wrapper>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- FAQ Section ----------

function FAQSection({ section, template }) {
  const fonts = getTemplateFonts(template);
  const content = section.content || {};
  const items = content.items || content.questions || [];
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div id="section-faq" className="py-16 md:py-24 px-6" style={{ backgroundColor: 'var(--wedding-secondary)' }}>
      <div className="max-w-3xl mx-auto">
        <h2
          className={`text-3xl md:text-4xl text-center mb-12 ${fonts.heading}`}
          style={{ color: 'var(--wedding-primary)' }}
        >
          {section.title || 'FAQ'}
        </h2>

        {items.length === 0 ? (
          <p className={`text-center text-gray-500 ${fonts.body}`}>Coming soon.</p>
        ) : (
          <div className="space-y-3">
            {items.map((item, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => toggle(i)}
                  className={`w-full flex items-center justify-between px-5 py-4 text-left ${fonts.body}`}
                >
                  <span className="font-medium text-gray-800 pr-4">
                    {item.question || item.q}
                  </span>
                  {openIndex === i ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openIndex === i && (
                  <div className={`px-5 pb-4 text-gray-600 text-sm leading-relaxed ${fonts.body}`}>
                    {item.answer || item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Section Renderer ----------

function renderSection(section, data, slug, template) {
  switch (section.section_type) {
    case 'hero':
      return <HeroSection key={section.id || 'hero'} section={section} page={data.page} template={template} />;
    case 'our_story':
      return <StorySection key={section.id || 'story'} section={section} template={template} />;
    case 'schedule':
      return <ScheduleSection key={section.id || 'schedule'} section={section} events={data.events} template={template} />;
    case 'wedding_party':
      return <PartySection key={section.id || 'party'} section={section} party={data.party} page={data.page} template={template} />;
    case 'travel':
      return <TravelSection key={section.id || 'travel'} section={section} template={template} />;
    case 'gallery':
      return <GallerySection key={section.id || 'gallery'} section={section} mediaItems={data.media} slug={slug} template={template} />;
    case 'rsvp':
      return <RSVPSection key={section.id || 'rsvp'} section={section} slug={slug} template={template} />;
    case 'vendors':
      return <VendorsSection key={section.id || 'vendors'} section={section} vendors={data.vendors} template={template} />;
    case 'faq':
      return <FAQSection key={section.id || 'faq'} section={section} template={template} />;
    default:
      return null;
  }
}

// ---------- Footer ----------

function WeddingFooter({ page, template }) {
  const fonts = getTemplateFonts(template);

  return (
    <footer className="py-8 px-6 text-center border-t border-gray-200">
      <p className={`text-gray-400 text-sm mb-2 ${fonts.body}`}>
        {page.partner1_name} & {page.partner2_name}
        {page.wedding_date && ` | ${formatDate(page.wedding_date)}`}
      </p>
      <a
        href="https://bridalconnect.co.za"
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-gray-400 hover:text-gray-500 transition-colors inline-flex items-center gap-1"
      >
        <Heart className="w-3 h-3" /> Powered by BridalConnect
      </a>
    </footer>
  );
}

// ---------- Back to Top ----------

function BackToTop() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShow(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!show) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 left-6 z-40 bg-white/90 backdrop-blur-sm shadow-lg rounded-full p-3 text-gray-500 hover:text-gray-700 transition-colors"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}

// ---------- Main Component ----------

export default function WeddingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [passwordProtected, setPasswordProtected] = useState(false);
  const [protectedPage, setProtectedPage] = useState(null);
  const [activeSection, setActiveSection] = useState('hero');

  const fetchPage = useCallback(async () => {
    try {
      setLoading(true);
      const res = await wedding.getPage(slug);
      const d = res.data;

      // Check if password protected
      if (d.is_password_protected && !d.sections) {
        setPasswordProtected(true);
        setProtectedPage(d);
      } else {
        setData(d);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Track active section on scroll
  useEffect(() => {
    if (!data?.sections) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionType = entry.target.id.replace('section-', '');
            setActiveSection(sectionType);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-10% 0px -60% 0px' }
    );

    const visibleSections = data.sections.filter(s => s.is_visible !== false);
    visibleSections.forEach((s) => {
      const el = document.getElementById(`section-${s.section_type}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [data]);

  // Handle password unlock
  const handleUnlock = (fullData) => {
    setPasswordProtected(false);
    setProtectedPage(null);
    setData(fullData);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-rose-400 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Loading wedding page...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-gray-300" />
          </div>
          <h1 className="text-2xl font-serif text-gray-800 mb-2">Wedding Page Not Found</h1>
          <p className="text-gray-500 mb-6">
            We couldn't find a wedding page at this address. The link may have changed or the page may have been removed.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Go to BridalConnect
          </Link>
        </div>
      </div>
    );
  }

  // Password gate
  if (passwordProtected && protectedPage) {
    return (
      <PasswordGate
        page={protectedPage}
        onUnlock={handleUnlock}
        template={protectedPage.template}
      />
    );
  }

  // No data fallback
  if (!data || !data.page) {
    return null;
  }

  const { page, sections = [] } = data;
  const template = page.template || 'classic';
  const colors = page.color_scheme || {};

  // Sort sections by sort_order
  const sortedSections = [...sections]
    .filter(s => s.is_visible !== false)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  // Apply CSS custom properties
  const pageStyle = {
    '--wedding-primary': colors.primary || '#D4507A',
    '--wedding-secondary': colors.secondary || '#F5E6CC',
    '--wedding-accent': colors.accent || colors.primary || '#D4507A',
    '--wedding-bg': colors.background || '#ffffff'
  };

  return (
    <div className="min-h-screen" style={pageStyle}>
      <SectionNav
        sections={sortedSections}
        activeSection={activeSection}
        template={template}
      />

      <main>
        {sortedSections.map((section) => renderSection(section, data, slug, template))}
      </main>

      <WeddingFooter page={page} template={template} />
      <BackToTop />
    </div>
  );
}
